import React from 'react';
import { HashRouter } from 'react-router-dom';
import { AuthForm } from './components/AuthForm';
import { Header } from './components/Header';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { ProfileScreen } from './components/ProfileScreen';
import { AlertsScreen } from './components/AlertsScreen';
import { ReferralsScreen } from './components/ReferralsScreen';
import { AdminScreen } from './components/AdminScreen';
import { ProtectedRoute } from './components/ProtectedRoute';
import { supabase } from './lib/supabase';

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [currentPath, setCurrentPath] = React.useState('/alerts');

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      if (session) {
        checkAdminStatus(session.user.id);
      }
    });

    supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    // Handle hash change for navigation
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || '/alerts';
      setCurrentPath(hash.startsWith('/') ? hash : `/${hash}`);
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Handle initial hash

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_role');

      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        return;
      }

      const isUserAdmin = data === 'admin';
      setIsAdmin(isUserAdmin);

      // If user is admin and not on admin path, redirect to admin
      if (isUserAdmin && currentPath !== '/admin') {
        window.location.hash = 'admin';
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    }
  };

  const renderAuthScreen = () => (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="text-center mb-8">
            <img 
              src="/assets/logo/logo_ft.png"
              alt="FT Viagens"
              className="h-24 mx-auto mb-6"
            />
            <p className="text-gray-600 px-4">
              Entre ou crie uma conta para começar a receber alertas com ofertas de passagens aéreas.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 mx-4">
            <AuthForm onSuccess={() => setIsAuthenticated(true)} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );

  const renderAdminScreen = () => (
    <ProtectedRoute requiredRole="admin">
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 pt-20 pb-6">
          <div className="max-w-6xl mx-auto px-4">
            <AdminScreen />
          </div>
        </main>
        <Footer />
      </div>
    </ProtectedRoute>
  );

  const renderScreen = () => {
    switch (currentPath) {
      case '/profile':
        return <ProfileScreen key="profile" />;
      case '/alerts':
        return <AlertsScreen key="alerts" />;
      case '/referrals':
        return <ReferralsScreen key="referrals" />;
      default:
        return <AlertsScreen key="alerts" />;
    }
  };

  const renderMainScreen = () => (
    <ProtectedRoute requiredRole="cliente">
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <Navigation currentPath={currentPath} />
        
        <main className="flex-1 pt-32 pb-24 md:pb-6">
          <div className="max-w-4xl mx-auto px-4">
            {renderScreen()}
          </div>
        </main>

        <Footer />
      </div>
    </ProtectedRoute>
  );

  return (
    <HashRouter>
      {!isAuthenticated ? renderAuthScreen() :
        isAdmin ? renderAdminScreen() : renderMainScreen()}
    </HashRouter>
  );
}